import React from 'react';
import { PlanConfig, ResultadoPlan } from '../../types/Calculadora';
import { formatCurrency2, formatNumber } from './calculadoraHelpers';
import NumberInput from './NumberInput';
import { Badge, Card } from '../ui';

interface Props {
  config: PlanConfig;
  resultado: ResultadoPlan;
  onChange: (patch: Partial<PlanConfig>) => void;
  /** Vuelve a la distribución etaria real de Salesforce. */
  onRestaurarDistribucion?: () => void;
  distribucionRealDisponible: boolean;
}

const Campo: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label,
  hint,
  children,
}) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[11px] text-gray-400 leading-snug">{hint}</p>}
  </div>
);

const PlanVariables: React.FC<Props> = ({
  config,
  resultado,
  onChange,
  onRestaurarDistribucion,
  distribucionRealDisponible,
}) => {
  const pctTotal = config.distribucionEdad.reduce((acc, t) => acc + t.pct, 0);
  const mixDesbalanceado = config.usaDistribucionEdad && Math.abs(pctTotal - 100) > 0.5;

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gappex-secondary">Variables del plan</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Población y uso de {config.nombre}
            {config.ficticio && (
              <Badge tone="info" className="ms-1.5 align-middle">
                Ficticio
              </Badge>
            )}
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer shrink-0">
          <input
            type="checkbox"
            checked={config.incluido}
            onChange={(e) => onChange({ incluido: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 text-gappex-primary focus:ring-gappex-primary cursor-pointer"
          />
          En el consolidado
        </label>
      </div>

      <div className="space-y-5">
        <Campo label="Cantidad de suscriptores">
          <NumberInput
            value={config.suscriptores}
            onChange={(v) => onChange({ suscriptores: v })}
            min={0}
            align="left"
            className="w-full"
            ariaLabel="Cantidad de suscriptores"
          />
        </Campo>

        {config.usaDistribucionEdad ? (
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs font-semibold text-gray-600">Mix por tramo de edad</span>
              {onRestaurarDistribucion && distribucionRealDisponible && (
                <button
                  type="button"
                  onClick={onRestaurarDistribucion}
                  className="text-[11px] font-medium text-gappex-primary hover:underline transition-colors"
                >
                  Restaurar el real
                </button>
              )}
            </div>

            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] text-gray-400 uppercase">
                  <th className="pb-1">Edad</th>
                  <th className="pb-1 text-right">Cuota</th>
                  <th className="pb-1 text-right w-24">% del plan</th>
                </tr>
              </thead>
              <tbody>
                {config.distribucionEdad.map((tramo, i) => (
                  <tr key={tramo.label}>
                    <td className="py-1 font-medium text-gray-600">{tramo.label}</td>
                    <td className="py-1 text-right">
                      <NumberInput
                        value={tramo.valor}
                        onChange={(v) => {
                          const next = [...config.distribucionEdad];
                          next[i] = { ...tramo, valor: v };
                          onChange({ distribucionEdad: next });
                        }}
                        min={0}
                        prefix="$"
                        currency
                        className="w-28 tabular-nums"
                        ariaLabel={`Cuota del tramo ${tramo.label}`}
                      />
                    </td>
                    <td className="py-1 text-right">
                      <NumberInput
                        value={tramo.pct}
                        onChange={(v) => {
                          const next = [...config.distribucionEdad];
                          next[i] = { ...tramo, pct: v };
                          onChange({ distribucionEdad: next });
                        }}
                        min={0}
                        max={100}
                        suffix="%"
                        className="w-20 tabular-nums"
                        ariaLabel={`Porcentaje del tramo ${tramo.label}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t">
                  <td className="pt-2 font-semibold text-gray-600" colSpan={2}>
                    Total
                  </td>
                  <td
                    className={`pt-2 text-right font-semibold tabular-nums ${
                      mixDesbalanceado ? 'text-amber-600' : 'text-gray-600'
                    }`}
                  >
                    {pctTotal.toFixed(1)}%
                  </td>
                </tr>
              </tfoot>
            </table>

            {mixDesbalanceado && (
              <p className="mt-2 text-[11px] text-amber-600 leading-snug">
                El mix no suma 100%. La cuota promedio se pondera por el total cargado, así que el
                resultado sigue siendo válido — pero conviene cuadrarlo.
              </p>
            )}

            <p className="mt-2 text-[11px] text-gray-400 leading-snug">
              Cuota promedio ponderada:{' '}
              <strong className="text-gray-600">{formatCurrency2(resultado.cuotaPromedio)}</strong> ×{' '}
              {formatNumber(config.suscriptores)} suscriptores
            </p>
          </div>
        ) : (
          <Campo label="Cuota mensual por suscriptor">
            <NumberInput
              value={config.cuotaMensual}
              onChange={(v) => onChange({ cuotaMensual: v })}
              min={0}
              align="left"
              prefix="$"
              currency
              className="w-full"
              ariaLabel="Cuota mensual por suscriptor"
            />
          </Campo>
        )}

        <Campo
          label={`Tasa de uso: ${(config.tasaUso * 100).toFixed(0)}%`}
          hint="Fracción de los suscriptores del plan que usa el servicio en el año. Las cantidades mínima y máxima de cada práctica son los usos anuales de esa gente."
        >
          <div className="flex items-center gap-3">
            <NumberInput
              value={config.tasaUso}
              onChange={(v) => onChange({ tasaUso: v })}
              min={0}
              max={1}
              className="w-20"
              ariaLabel="Tasa de uso"
            />
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={config.tasaUso}
              onChange={(e) => onChange({ tasaUso: Number(e.target.value) })}
              aria-label="Tasa de uso (deslizador)"
              className="flex-1 accent-sky-600 cursor-pointer"
            />
          </div>
        </Campo>
      </div>
    </Card>
  );
};

export default PlanVariables;
